import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `CellMenu` (UI r3 deliverable 34): one choice in a table cell, such as the
 * type a Notion database imports as. The trigger fills the cell at the row's
 * height (its icon and label, or the muted placeholder, and a chevron); the
 * popup is r2's `MenuPopup`: `menuitemradio` items with `aria-checked`, group
 * headings, disabled options with their reason, actions after a separator,
 * a `menu` layer that returns focus, scrolling past 12 rows, type-ahead.
 */
import { useId, useMemo, useRef, useState } from 'react';
import { Icon } from '../icon/icon.js';
import { cx } from './infra.js';
import { MenuPopup } from './menu.js';
/** The menu entries for `options`: headings where a group starts, then the actions. */
export function cellMenuEntries(options, value, onChange, actions = []) {
    const entries = [];
    let group;
    for (const o of options) {
        if (o.group !== group) {
            // A heading's group runs to the next heading or separator.
            if (o.group === undefined)
                entries.push('separator');
            else
                entries.push({ heading: o.group });
            group = o.group;
        }
        const item = {
            label: o.label,
            checked: value !== null && o.value === value,
            onSelect: () => onChange(o.value),
        };
        if (o.icon)
            item.icon = o.icon;
        if (o.description)
            item.description = o.description;
        if (o.disabled)
            item.disabled = true;
        if (o.disabledReason)
            item.disabledReason = o.disabledReason;
        entries.push(item);
    }
    if (actions.length > 0)
        entries.push('separator', ...actions);
    return entries;
}
export function CellMenu({ label, options, value, onChange, placeholder = 'Choose…', actions, error, testId, align = 'start', }) {
    const [open, setOpen] = useState(false);
    const anchor = useRef(null);
    const errorId = useId();
    const chosen = options.find((o) => o.value === value);
    const items = useMemo(() => cellMenuEntries(options, value, onChange, actions), [options, value, onChange, actions]);
    return (_jsxs("span", { className: "cx-cell-menu-wrap", children: [_jsxs("button", { ref: anchor, type: "button", className: "cx-cell-menu", "aria-label": `${label}: ${chosen?.label ?? placeholder}`, "aria-haspopup": "menu", "aria-expanded": open, "aria-describedby": error ? errorId : undefined, "data-invalid": error ? '' : undefined, "data-testid": testId, onClick: () => setOpen((o) => !o), children: [chosen?.icon && _jsx(Icon, { name: chosen.icon }), _jsx("span", { className: cx('cx-cell-menu__value', !chosen && 'cx-cell-menu__value--placeholder'), children: chosen?.label ?? placeholder }), _jsx(Icon, { name: "caret-down", size: 14, className: "cx-cell-menu__chevron" })] }), error && (_jsx("span", { id: errorId, className: "cx-cell-menu__error", children: error })), _jsx(MenuPopup, { open: open, onClose: () => setOpen(false), anchor: anchor, label: label, items: items, align: align })] }));
}
//# sourceMappingURL=cell-menu.js.map